import {
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { listPlatformIntegrations } from '../../api/admin/endpoints';
import type { PlatformIntegrationLink } from '../../api/admin/types';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime, formatNumber } from '../../components/format';
import { IntegrationStatusChip } from '../../components/IntegrationStatusChip';
import { PageHeader } from '../../components/PageHeader';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { sortRows, useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

type LinkSortKey =
  'tenant' | 'name' | 'status' | 'lastSeenAt' | 'unmatched' | 'openConflicts' | 'pendingDeliveries';

const VALUE_OF: Record<LinkSortKey, (link: PlatformIntegrationLink) => string | number | null> = {
  tenant: (link) => link.tenantName,
  name: (link) => link.name,
  status: (link) => link.status,
  lastSeenAt: (link) => link.lastSeenAt,
  unmatched: (link) => link.unmatched,
  openConflicts: (link) => link.openConflicts,
  pendingDeliveries: (link) => link.pendingDeliveries,
};

type StatusFilter = PlatformIntegrationLink['status'] | '';

/** Every linked e-shop of every tenant (R0009): who uses integrations, and which shop needs attention. */
export const IntegrationsPage = () => {
  const { language, t } = useLanguage();
  const query = useQuery({ queryKey: ['platform-integrations'], queryFn: listPlatformIntegrations });
  const [term, setTerm] = useState('');
  const [status, setStatus] = useState<StatusFilter>('');
  const [troubled, setTroubled] = useState(false);
  const sort = useSort<LinkSortKey>('tenant');

  const needle = term.trim().toLocaleLowerCase('sk');
  const rows = sortRows(
    (query.data ?? []).filter(
      (link) =>
        (status === '' || link.status === status) &&
        (!troubled || link.openConflicts > 0 || link.pendingDeliveries > 0 || link.unmatched > 0) &&
        (needle === '' ||
          [link.tenantName, link.companyName, link.name, link.shopUrl].some((value) =>
            value?.toLocaleLowerCase('sk').includes(needle),
          )),
    ),
    sort.direction,
    VALUE_OF[sort.key],
  );

  return (
    <>
      <PageHeader title={t('platformIntegrations.title')} subtitle={t('platformIntegrations.subtitle')} />
      <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      <Paper variant="outlined">
        <TableFilters>
          <TextField
            size="small"
            label={t('common.search')}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          <TextField
            select
            size="small"
            label={t('field.status')}
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {(['PENDING', 'ACTIVE', 'DISABLED', 'REVOKED'] as const).map((value) => (
              <MenuItem key={value} value={value}>
                {t(`linkStatus.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label={t('platformIntegrations.attention')}
            value={troubled ? 'yes' : ''}
            onChange={(e) => setTroubled(e.target.value === 'yes')}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="yes">{t('platformIntegrations.needsAttention')}</MenuItem>
          </TextField>
        </TableFilters>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableCell sort={sort} column="tenant">
                  {t('platformIntegrations.tenant')}
                </SortableCell>
                <SortableCell sort={sort} column="name">
                  {t('integrations.shop')}
                </SortableCell>
                <SortableCell sort={sort} column="status">
                  {t('field.status')}
                </SortableCell>
                <SortableCell sort={sort} column="lastSeenAt">
                  {t('integrations.lastSeen')}
                </SortableCell>
                <SortableCell sort={sort} column="unmatched" align="right">
                  {t('integrations.unmatched')}
                </SortableCell>
                <SortableCell sort={sort} column="openConflicts" align="right">
                  {t('integrations.openConflicts')}
                </SortableCell>
                <SortableCell sort={sort} column="pendingDeliveries" align="right">
                  {t('integrations.pending')}
                </SortableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.isLoading ? (
                <TableSkeletonRows columns={7} />
              ) : (
                rows.map((link) => (
                  <TableRow key={link.id} hover>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/platform/tenants/${link.tenantId}`}
                        underline="hover"
                      >
                        {link.tenantName}
                      </Link>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {link.companyName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {link.name}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {link.shopUrl ?? t('integrations.notConnected')}
                        {link.platformVersion
                          ? ` · ${t('detail.versionsValue', {
                              platform: t(`platform.${link.platform}`),
                              version: link.platformVersion,
                              connector: link.connectorVersion ?? '—',
                            })}`
                          : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IntegrationStatusChip status={link.status} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDateTime(link.lastSeenAt, language)}
                    </TableCell>
                    <TableCell align="right">{formatNumber(link.unmatched, language)}</TableCell>
                    <TableCell
                      align="right"
                      sx={{ color: link.openConflicts > 0 ? 'warning.main' : undefined }}
                    >
                      {formatNumber(link.openConflicts, language)}
                    </TableCell>
                    <TableCell align="right">{formatNumber(link.pendingDeliveries, language)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!query.isLoading && rows.length === 0 ? (
          <EmptyState
            title={t('platformIntegrations.empty')}
            detail={t('platformIntegrations.emptyDetail')}
          />
        ) : null}
      </Paper>
    </>
  );
};
